import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react';
import { cn } from '@/shared/helpers/cn';
import { Spinner } from '@/shared/components/Spinner';
import styles from './Button.module.scss';

export type ButtonVariant = 'primary' | 'secondary' | 'tertiary' | 'danger' | 'dangerSoft';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type'> {
  variant?: ButtonVariant;
  /** `sm` 32px dentro de tabela · `md` 40px padrao · `lg` 48px CTA e mobile. */
  size?: ButtonSize;
  /** Mostra o spinner no lugar do icone e bloqueia o clique, preservando a largura. */
  loading?: boolean;
  /** Icone a esquerda do rotulo. Decorativo — o texto e quem nomeia a acao. */
  icon?: ReactNode;
  /** Ocupa toda a largura disponivel. */
  block?: boolean;
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Botao do sistema.
 *
 * O raio `pill` fica reservado a acao primaria — e assinatura da marca, e usar em
 * tudo achata a hierarquia. Uma acao primaria por tela.
 *
 * O rotulo diz o que acontece ("Salvar alterações", nao "Enviar") e usa sentence
 * case, com o mesmo verbo atravessando o fluxo ate o toast de confirmacao.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = 'primary', size = 'md', loading = false, icon, block, className, children, disabled, type = 'button', ...rest },
  ref,
) {
  // Botao so de icone (o nome vem do `aria-label`) e quadrado: com o
  // padding lateral do rotulo ele viraria uma elipse, e o icone deixaria de
  // ficar no centro.
  const somenteIcone = children === undefined || children === null || children === '';

  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        styles.button,
        styles[variant],
        styles[size],
        block && styles.block,
        somenteIcone && styles.iconOnly,
        className,
      )}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? (
        <Spinner size={size === 'sm' ? 14 : 16} className={styles.icon} />
      ) : icon ? (
        <span className={styles.icon} aria-hidden="true">
          {icon}
        </span>
      ) : null}
      {somenteIcone ? null : <span className={styles.label}>{children}</span>}
    </button>
  );
});
